import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useBlocker, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import {
  AlignLeft,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  FileText,
  GripVertical,
  Image as ImageIcon,
  Loader2,
  Plus,
  Save,
  Trash2,
  Upload,
  Video,
  X,
} from "lucide-react";
import { parseVimeoRef } from "@/lib/cases/parseModules";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { confirmDialog } from "@/lib/confirm-dialog";
import {
  PUBLIC_DESCRIPTION_SOFT_LIMIT,
  describedLabel,
  hasDescription,
  isDescriptionDirty,
  isOverSoftLimit,
  normalizeDescription,
} from "@/lib/course-descriptions";

/** Ticket W13: Anzeigezustand des Beschreibungs-Umschalters je Nutzer. */
const DESCRIPTIONS_TOGGLE_KEY = "admin.course-editor.show-descriptions";

type DescriptionCtx = {
  show: boolean;
  saveSection: (sectionId: string, value: string | null) => Promise<boolean>;
  saveLesson: (lesson: LessonRow, value: string | null) => Promise<boolean>;
  setDirty: (key: string, dirty: boolean) => void;
};

const DescriptionContext = createContext<DescriptionCtx | null>(null);

function useDescriptions(): DescriptionCtx {
  const ctx = useContext(DescriptionContext);
  if (!ctx) throw new Error("DescriptionContext fehlt");
  return ctx;
}


type CourseRow = Database["public"]["Tables"]["courses"]["Row"];
type SectionRow = Database["public"]["Tables"]["course_sections"]["Row"];
type LessonRow = Database["public"]["Tables"]["course_lessons"]["Row"];
type MaterialRow = Database["public"]["Tables"]["course_lesson_materials"]["Row"];
type CourseStatus = Database["public"]["Enums"]["course_status"];
/** Ticket W15: Videokurs (mit Struktur) oder Kompaktkurs (genau ein Video). */
type CourseFormat = Database["public"]["Enums"]["service_course_format"];

export const Route = createFileRoute("/_admin/admin/courses/$id")({
  component: AdminCourseEditor,
});

function slugifyLive(s: string) {
  return s.toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .replace(/[^a-z0-9-]+/g, "-");
}
function slugify(s: string) {
  return slugifyLive(s).replace(/(^-|-$)/g, "");
}


const MATERIAL_MAX_BYTES = 25 * 1024 * 1024;
const COVER_MAX_BYTES = 5 * 1024 * 1024;
const COVER_ALLOWED = ["image/jpeg", "image/png", "image/webp"];
const COVER_RECOMMENDED_WIDTH = 1600;
const COVER_RECOMMENDED_HEIGHT = 900;
const COVER_MIN_WIDTH = 1200;
const COVER_HINT = `Empfohlen: ${COVER_RECOMMENDED_WIDTH} × ${COVER_RECOMMENDED_HEIGHT} px (16:9), JPG, PNG oder WebP, max. 5 MB.`;

function formatMb(bytes: number) {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function readImageSize(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}
const MATERIAL_ALLOWED = [
  "application/pdf",
  "application/zip",
  "application/x-zip-compressed",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/svg+xml",
];

function AdminCourseEditor() {
  const { id } = Route.useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState<CourseRow | null>(null);
  const [sections, setSections] = useState<SectionRow[]>([]);
  const [lessonsBySection, setLessonsBySection] = useState<Record<string, LessonRow[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [coverDims, setCoverDims] = useState<{ width: number; height: number } | null>(null);
  const [coverWarning, setCoverWarning] = useState<string | null>(null);
  const [openLesson, setOpenLesson] = useState<{ lesson: LessonRow; sectionId: string } | null>(null);

  // Draft fields for the course
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState("");
  // Ticket W15: Format des Kurses. Kompaktkurs = genau ein Video, keine Struktur.
  const [formatSaving, setFormatSaving] = useState(false);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2500);
  }, []);

  // ===== Ticket W13: öffentliche Beschreibungen in der Struktur-Ansicht =====

  const [showDescriptions, setShowDescriptions] = useState(false);
  const [dirtyFields, setDirtyFields] = useState<Record<string, true>>({});
  // Speichervorgänge je Datensatz nacheinander, damit ein langsamer erster
  // Vorgang keinen neueren überschreibt.
  const saveChains = useRef(new Map<string, Promise<unknown>>());

  useEffect(() => {
    if (localStorage.getItem(DESCRIPTIONS_TOGGLE_KEY) === "1") setShowDescriptions(true);
  }, []);

  const toggleDescriptions = useCallback(() => {
    setShowDescriptions((prev) => {
      const next = !prev;
      localStorage.setItem(DESCRIPTIONS_TOGGLE_KEY, next ? "1" : "0");
      return next;
    });
  }, []);

  const setDirty = useCallback((key: string, dirty: boolean) => {
    setDirtyFields((prev) => {
      const has = !!prev[key];
      if (has === dirty) return prev;
      const next = { ...prev };
      if (dirty) next[key] = true;
      else delete next[key];
      return next;
    });
  }, []);

  const unsavedCount = Object.keys(dirtyFields).length;

  // Seite verlassen: Rückfrage, solange eine Eingabe offen ist.
  useEffect(() => {
    if (unsavedCount === 0) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [unsavedCount]);

  useBlocker({
    shouldBlockFn: () =>
      unsavedCount > 0 &&
      !window.confirm("Eine Beschreibung ist noch nicht gespeichert. Seite trotzdem verlassen?"),
    enableBeforeUnload: false,
  });

  const enqueueSave = useCallback(<T,>(key: string, run: () => Promise<T>): Promise<T> => {
    const prev = saveChains.current.get(key) ?? Promise.resolve();
    const next = prev.catch(() => undefined).then(run);
    saveChains.current.set(key, next);
    return next;
  }, []);

  const saveSectionDescription = useCallback(
    (sectionId: string, value: string | null) =>
      enqueueSave(`section:${sectionId}`, async () => {
        const { data, error: err } = await supabase
          .from("course_sections")
          .update({ description: value })
          .eq("id", sectionId)
          .select("*")
          .single();
        if (err || !data) {
          setError(`Beschreibung nicht gespeichert: ${err?.message ?? "Unbekannter Fehler"}`);
          return false;
        }
        setSections((prev) => prev.map((s) => (s.id === data.id ? data : s)));
        showToast("Beschreibung gespeichert");
        return true;
      }),
    [enqueueSave, showToast],
  );

  const saveLessonDescription = useCallback(
    (lesson: LessonRow, value: string | null) =>
      enqueueSave(`lesson:${lesson.id}`, async () => {
        const { data, error: err } = await supabase
          .from("course_lessons")
          .update({ description: value })
          .eq("id", lesson.id)
          .select("*")
          .single();
        if (err || !data) {
          setError(
            `Beschreibung der Lektion „${lesson.title || "Ohne Titel"}" nicht gespeichert: ${err?.message ?? "Unbekannter Fehler"}`,
          );
          return false;
        }
        setLessonsBySection((prev) => {
          const list = prev[data.section_id] ?? [];
          return { ...prev, [data.section_id]: list.map((l) => (l.id === data.id ? data : l)) };
        });
        showToast("Beschreibung gespeichert");
        return true;
      }),
    [enqueueSave, showToast],
  );

  const descriptionCtx = useMemo<DescriptionCtx>(
    () => ({
      show: showDescriptions,
      saveSection: saveSectionDescription,
      saveLesson: saveLessonDescription,
      setDirty,
    }),
    [showDescriptions, saveSectionDescription, saveLessonDescription, setDirty],
  );


  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [{ data: c, error: cErr }, { data: sec, error: sErr }, { data: les, error: lErr }] = await Promise.all([
      supabase.from("courses").select("*").eq("id", id).single(),
      supabase.from("course_sections").select("*").eq("course_id", id).order("sort_order", { ascending: true }),
      supabase.from("course_lessons").select("*").eq("course_id", id).order("sort_order", { ascending: true }),
    ]);
    if (cErr || !c) {
      setError(cErr?.message ?? "Kurs nicht gefunden.");
      setLoading(false);
      return;
    }
    if (sErr) { setError(sErr.message); setLoading(false); return; }
    if (lErr) { setError(lErr.message); setLoading(false); return; }
    setCourse(c);
    setTitle(c.title);
    setSlug(c.slug);
    setSlugTouched(true);
    setDescription(c.description ?? "");
    setSections(sec ?? []);
    const grouped: Record<string, LessonRow[]> = {};
    for (const l of les ?? []) {
      (grouped[l.section_id] ||= []).push(l);
    }
    setLessonsBySection(grouped);
    setLoading(false);
  }, [id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Refresh signed cover URL when path changes
  useEffect(() => {
    let cancelled = false;
    if (!course?.cover_image_path) { setCoverUrl(null); return; }
    supabase.storage.from("course-covers").createSignedUrl(course.cover_image_path, 60 * 60).then(({ data }) => {
      if (!cancelled) setCoverUrl(data?.signedUrl ?? null);
    });
    return () => { cancelled = true; };
  }, [course?.cover_image_path]);

  async function saveCourseFields() {
    if (!course) return;
    const cleanSlug = slug ? slugify(slug) : "";
    if (!title.trim()) { setError("Titel darf nicht leer sein."); return; }
    if (!cleanSlug || !/^[a-z0-9-]+$/.test(cleanSlug)) { setError("Slug ist ungültig (nur a–z, 0–9, Bindestrich)."); return; }
    setSaving(true);
    setError(null);
    const { error: err, data } = await supabase
      .from("courses")
      .update({ title: title.trim(), slug: cleanSlug, description: description.trim() })
      .eq("id", course.id)
      .select("*")
      .single();
    setSaving(false);
    if (err) { setError(err.message); return; }
    if (data) setCourse(data);
    showToast("Kurs gespeichert");
  }

  async function togglePublished() {
    if (!course) return;
    const next: CourseStatus = course.status === "published" ? "draft" : "published";
    const patch: Partial<CourseRow> = { status: next };
    if (next === "published" && !course.published_at) patch.published_at = new Date().toISOString();
    const { data, error } = await supabase
      .from("courses")
      .update(patch)
      .eq("id", course.id)
      .select("*")
      .single();
    if (error) { setError(error.message); return; }
    if (data) setCourse(data);
    showToast(next === "published" ? "Veröffentlicht" : "Auf Entwurf gesetzt");
  }

  /**
   * Ticket W15: Formatwechsel. Der Wechsel auf Kompaktkurs wird abgelehnt,
   * solange mehr als ein Abschnitt oder mehr als eine Lektion vorhanden ist —
   * gelöscht wird nichts. Umgekehrt ist der Wechsel immer erlaubt.
   */
  async function setCourseFormat(next: CourseFormat) {
    if (!course || next === course.course_format) return;
    setError(null);
    const lessonCount = Object.values(lessonsBySection).reduce((n, l) => n + l.length, 0);
    if (next === "kompaktkurs" && (sections.length > 1 || lessonCount > 1)) {
      setError(
        `Dieser Kurs hat ${lessonCount} Lektion(en) in ${sections.length} Abschnitt(en). ` +
          "Ein Kompaktkurs besteht aus genau einem Video. Entfernen Sie zuerst die überzähligen Lektionen.",
      );
      return;
    }
    setFormatSaving(true);
    const { data, error: err } = await supabase
      .from("courses")
      .update({ course_format: next })
      .eq("id", course.id)
      .select("*")
      .single();
    if (err || !data) {
      setFormatSaving(false);
      setError(err?.message ?? "Format nicht gespeichert.");
      return;
    }
    setCourse(data);
    if (next === "kompaktkurs") await ensureCompactLesson(data.id);
    setFormatSaving(false);
    showToast(next === "kompaktkurs" ? "Kurs ist jetzt ein Kompaktkurs" : "Kurs ist jetzt ein Videokurs");
  }

  /** Ticket W15: Kompaktkurs braucht genau einen Abschnitt mit einer Lektion. */
  async function ensureCompactLesson(courseId: string) {
    let section = sections[0] ?? null;
    if (!section) {
      const { data, error: err } = await supabase
        .from("course_sections")
        .insert({ course_id: courseId, title: title.trim() || "Kurs", sort_order: 0 })
        .select("*")
        .single();
      if (err || !data) { setError(err?.message ?? "Abschnitt nicht angelegt."); return; }
      section = data;
      setSections([data]);
    }
    const existing = lessonsBySection[section.id] ?? [];
    if (existing.length > 0) return;
    const { data: lesson, error: lErr } = await supabase
      .from("course_lessons")
      .insert({
        section_id: section.id,
        course_id: courseId,
        title: title.trim() || "Kurs",
        body_md: "",
        sort_order: 0,
      })
      .select("*")
      .single();
    if (lErr || !lesson) { setError(lErr?.message ?? "Lektion nicht angelegt."); return; }
    setLessonsBySection({ [section.id]: [lesson] });
  }

  async function onCoverUpload(file: File) {
    if (!course) return;
    setError(null);
    setCoverWarning(null);

    if (!COVER_ALLOWED.includes(file.type)) {
      setError(`Dieses Dateiformat wird nicht unterstützt (${file.type || "unbekannt"}). Erlaubt sind JPG, PNG und WebP.`);
      return;
    }
    if (file.size > COVER_MAX_BYTES) {
      setError(`Die Datei ist ${formatMb(file.size)} groß. Maximal erlaubt sind 5 MB. ${COVER_HINT}`);
      return;
    }

    const dims = await readImageSize(file);
    if (dims) {
      const ratio = dims.width / dims.height;
      const warnings: string[] = [];
      if (dims.width < COVER_MIN_WIDTH) {
        warnings.push(`Das Bild ist nur ${dims.width} px breit – empfohlen sind ${COVER_RECOMMENDED_WIDTH} px.`);
      }
      if (Math.abs(ratio - 16 / 9) > 0.12) {
        warnings.push("Das Seitenverhältnis weicht von 16:9 ab, das Bild wird in der Kachel beschnitten.");
      }
      setCoverWarning(warnings.length ? warnings.join(" ") : null);
    }

    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const path = `${course.id}/cover-${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage.from("course-covers").upload(path, file, { upsert: false });
    if (upErr) { setError(upErr.message); return; }
    setCoverDims(dims);
    // Delete old
    if (course.cover_image_path) {
      await supabase.storage.from("course-covers").remove([course.cover_image_path]);
    }
    const { data, error } = await supabase
      .from("courses")
      .update({ cover_image_path: path })
      .eq("id", course.id)
      .select("*")
      .single();
    if (error) { setError(error.message); return; }
    if (data) setCourse(data);
    showToast("Cover aktualisiert");
  }

  async function onCoverRemove() {
    if (!course?.cover_image_path) return;
    await supabase.storage.from("course-covers").remove([course.cover_image_path]);
    const { data, error } = await supabase
      .from("courses")
      .update({ cover_image_path: null })
      .eq("id", course.id)
      .select("*")
      .single();
    if (error) { setError(error.message); return; }
    if (data) setCourse(data);
    setCoverUrl(null);
    setCoverDims(null);
    setCoverWarning(null);
  }

  async function onDeleteCourse() {
    if (!course) return;
    if (
      !(await confirmDialog({
        title: `Kurs „${course.title}" löschen?`,
        description:
          "Sektionen, Lektionen und Materialien werden mitgelöscht. Das lässt sich nicht rückgängig machen.",
      }))
    )
      return;
    // Check assignments
    const { count } = await supabase
      .from("course_assignments")
      .select("id", { count: "exact", head: true })
      .eq("course_id", course.id);
    if ((count ?? 0) > 0) {
      alert("Kurs ist Kunden zugewiesen. Bitte zuerst die Zuweisungen entfernen.");
      return;
    }
    const { error } = await supabase.from("courses").delete().eq("id", course.id);
    if (error) { setError(error.message); return; }
    navigate({ to: "/admin/courses" });
  }

  // Sections
  async function addSection() {
    if (!course) return;
    const nextOrder = sections.length > 0 ? Math.max(...sections.map((s) => s.sort_order)) + 1 : 0;
    const { data, error } = await supabase
      .from("course_sections")
      .insert({ course_id: course.id, title: "Neue Sektion", sort_order: nextOrder })
      .select("*")
      .single();
    if (error) { setError(error.message); return; }
    if (data) setSections((prev) => [...prev, data]);
  }

  async function renameSection(sectionId: string, title: string) {
    const { data, error } = await supabase
      .from("course_sections")
      .update({ title })
      .eq("id", sectionId)
      .select("*")
      .single();
    if (error) { setError(error.message); return; }
    if (data) setSections((prev) => prev.map((s) => (s.id === data.id ? data : s)));
  }

  async function deleteSection(section: SectionRow) {
    const lessons = lessonsBySection[section.id] ?? [];
    if (
      !(await confirmDialog({
        title: `Sektion „${section.title}" löschen?`,
        description:
          lessons.length > 0
            ? `Die Sektion enthält ${lessons.length} Lektion(en). Diese werden mitgelöscht.`
            : "Die Sektion wird dauerhaft entfernt.",
      }))
    )
      return;
    const { error } = await supabase.from("course_sections").delete().eq("id", section.id);
    if (error) { setError(error.message); return; }
    setSections((prev) => prev.filter((s) => s.id !== section.id));
    setLessonsBySection((prev) => {
      const next = { ...prev };
      delete next[section.id];
      return next;
    });
  }

  async function reorderSections(orderedIds: string[]) {
    setSections((prev) => {
      const byId = new Map(prev.map((s) => [s.id, s]));
      return orderedIds.map((id, i) => ({ ...(byId.get(id) as SectionRow), sort_order: i }));
    });
    // Two-phase update to avoid unique-constraint collisions on (course_id, sort_order)
    // even though it's DEFERRABLE — we bump to large offsets, then set final values.
    const offset = 100000;
    await Promise.all(orderedIds.map((id, i) =>
      supabase.from("course_sections").update({ sort_order: offset + i }).eq("id", id),
    ));
    await Promise.all(orderedIds.map((id, i) =>
      supabase.from("course_sections").update({ sort_order: i }).eq("id", id),
    ));
  }

  // Lessons
  async function addLesson(sectionId: string) {
    if (!course) return;
    const existing = lessonsBySection[sectionId] ?? [];
    const nextOrder = existing.length > 0 ? Math.max(...existing.map((l) => l.sort_order)) + 1 : 0;
    const { data, error } = await supabase
      .from("course_lessons")
      .insert({
        section_id: sectionId,
        course_id: course.id,
        title: "Neue Lektion",
        body_md: "",
        sort_order: nextOrder,
      })
      .select("*")
      .single();
    if (error) { setError(error.message); return; }
    if (data) {
      setLessonsBySection((prev) => ({ ...prev, [sectionId]: [...(prev[sectionId] ?? []), data] }));
      setOpenLesson({ lesson: data, sectionId });
    }
  }

  async function deleteLesson(sectionId: string, lesson: LessonRow) {
    if (
      !(await confirmDialog({
        title: `Lektion „${lesson.title}" löschen?`,
        description: "Die Lektion und ihr Lernfortschritt werden dauerhaft entfernt.",
      }))
    )
      return;
    const { error } = await supabase.from("course_lessons").delete().eq("id", lesson.id);
    if (error) { setError(error.message); return; }
    setLessonsBySection((prev) => ({
      ...prev,
      [sectionId]: (prev[sectionId] ?? []).filter((l) => l.id !== lesson.id),
    }));
  }

  async function reorderLessons(sectionId: string, orderedIds: string[]) {
    setLessonsBySection((prev) => {
      const list = prev[sectionId] ?? [];
      const byId = new Map(list.map((l) => [l.id, l]));
      return { ...prev, [sectionId]: orderedIds.map((id, i) => ({ ...(byId.get(id) as LessonRow), sort_order: i })) };
    });
    const offset = 100000;
    await Promise.all(orderedIds.map((id, i) =>
      supabase.from("course_lessons").update({ sort_order: offset + i }).eq("id", id),
    ));
    await Promise.all(orderedIds.map((id, i) =>
      supabase.from("course_lessons").update({ sort_order: i }).eq("id", id),
    ));
  }

  function onLessonUpdated(updated: LessonRow) {
    setLessonsBySection((prev) => ({
      ...prev,
      [updated.section_id]: (prev[updated.section_id] ?? []).map((l) => (l.id === updated.id ? updated : l)),
    }));
    if (openLesson && openLesson.lesson.id === updated.id) {
      setOpenLesson({ lesson: updated, sectionId: updated.section_id });
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Kurs wird geladen…
      </div>
    );
  }

  if (!course) {
    return (
      <div className="p-8">
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error ?? "Kurs nicht gefunden."}
        </div>
        <Link to="/admin/courses" className="mt-4 inline-flex items-center gap-2 text-sm text-primary">
          <ArrowLeft className="h-4 w-4" /> Zurück zur Kursliste
        </Link>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center gap-3">
        <Link to="/admin/courses" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Zurück
        </Link>
        <span className="text-muted-foreground">/</span>
        <span className="text-sm text-foreground">Kurs bearbeiten</span>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Kopf-Formular */}
      <section className="mb-6 rounded-lg border border-border bg-background p-5">
        <div className="grid gap-6 md:grid-cols-[1fr_320px]">
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Titel</label>
              <input
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (!slugTouched) setSlug(slugifyLive(e.target.value));
                }}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Slug</label>
              <input
                value={slug}
                onChange={(e) => { setSlug(slugifyLive(e.target.value)); setSlugTouched(true); }}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
              />
              <p className="mt-1 text-xs text-muted-foreground">Nur a–z, 0–9 und Bindestrich.</p>
            </div>
            {/* Ticket W15: Format des Kurses – einzige Quelle, der Katalogeintrag zeigt es nur an. */}
            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground" htmlFor="course-format">
                Format
              </label>
              <select
                id="course-format"
                value={course.course_format}
                disabled={formatSaving}
                onChange={(e) => setCourseFormat(e.target.value as CourseFormat)}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-60"
              >
                <option value="videokurs">Videokurs (Abschnitte und Lektionen)</option>
                <option value="kompaktkurs">Kompaktkurs (genau ein Video)</option>
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                {course.course_format === "kompaktkurs"
                  ? "Ein Kompaktkurs zeigt keine Kapitelübersicht. Die öffentliche Beschreibung ist das Beschreibungsfeld dieses Kurses."
                  : "Ein Videokurs wird über Abschnitte und Lektionen gepflegt."}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Beschreibung</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={saveCourseFields}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Speichern
              </button>
              <button
                onClick={togglePublished}
                className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold ${
                  course.status === "published"
                    ? "bg-secondary text-foreground hover:bg-secondary/70"
                    : "bg-brand-button text-brand-button-foreground"
                }`}
              >
                {course.status === "published" ? "Auf Entwurf setzen" : "Veröffentlichen"}
              </button>
              <span className="text-xs text-muted-foreground">
                {course.status === "published"
                  ? course.published_at
                    ? `Veröffentlicht am ${new Intl.DateTimeFormat("de-DE").format(new Date(course.published_at))}`
                    : "Veröffentlicht"
                  : "Entwurf"}
              </span>
              <button
                onClick={onDeleteCourse}
                className="ml-auto inline-flex items-center gap-1 rounded-md border border-destructive/40 px-3 py-2 text-xs text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-3.5 w-3.5" /> Kurs löschen
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Cover</label>
            <div className="mt-1 aspect-video overflow-hidden rounded-md border border-border bg-secondary/40">
              {coverUrl ? (
                <img src={coverUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                  <ImageIcon className="h-6 w-6" />
                </div>
              )}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{COVER_HINT}</p>
            {coverDims && (
              <p className="mt-1 text-xs text-muted-foreground">
                Hochgeladen: {coverDims.width} × {coverDims.height} px
              </p>
            )}
            {coverWarning && (
              <p className="mt-1 text-xs text-brand-accent">{coverWarning}</p>
            )}
            <div className="mt-2 flex items-center gap-2">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-input px-3 py-1.5 text-xs hover:bg-secondary">
                <Upload className="h-3.5 w-3.5" /> Cover hochladen
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onCoverUpload(f);
                    e.target.value = "";
                  }}
                />
              </label>
              {course.cover_image_path && (
                <button
                  onClick={onCoverRemove}
                  className="inline-flex items-center gap-1 rounded-md border border-input px-3 py-1.5 text-xs text-muted-foreground hover:text-destructive"
                >
                  <X className="h-3.5 w-3.5" /> Entfernen
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Ticket W15: Struktur nur bei Videokursen. */}
      {course.course_format === "videokurs" ? (
        <>
        {/* Struktur */}
        <section className="rounded-lg border border-border bg-background p-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Struktur</h2>
              <p className="text-xs text-muted-foreground">Sektionen und Lektionen per Drag &amp; Drop sortieren.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleDescriptions}
                type="button"
                aria-pressed={showDescriptions}
                className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium ${
                  showDescriptions
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-input text-muted-foreground hover:text-foreground"
                }`}
              >
                <AlignLeft className="h-4 w-4" /> Beschreibungen anzeigen
              </button>
              <button
                onClick={addSection}
                className="inline-flex items-center gap-2 rounded-md bg-secondary px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary/70"
              >
                <Plus className="h-4 w-4" /> Sektion hinzufügen
              </button>
            </div>
          </div>

          {showDescriptions && (
            <p className="mb-3 rounded-md bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
              Die öffentliche Beschreibung erscheint in der Kapitelübersicht auf der
              Verkaufsseite. Der Lektionsinhalt für Kundinnen und Kunden bleibt im
              Lektions-Dialog. Gespeichert wird beim Verlassen des Feldes.
            </p>
          )}

          {sections.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Noch keine Sektionen. Beginnen Sie mit der ersten Sektion.
            </div>
          ) : (
            <DescriptionContext.Provider value={descriptionCtx}>
              <SectionList
                sections={sections}
                lessonsBySection={lessonsBySection}
                onReorderSections={reorderSections}
                onReorderLessons={reorderLessons}
                onRenameSection={renameSection}
                onDeleteSection={deleteSection}
                onAddLesson={addLesson}
                onDeleteLesson={deleteLesson}
                onOpenLesson={(lesson, sectionId) => setOpenLesson({ lesson, sectionId })}
              />
            </DescriptionContext.Provider>
          )}
        </section>
        </>
      ) : (
        <CompactVideoBlock
          lesson={(sections[0] && lessonsBySection[sections[0].id]?.[0]) ?? null}
          onUpdated={onLessonUpdated}
          onOpenDetails={(lesson) =>
            setOpenLesson({ lesson, sectionId: lesson.section_id })
          }
          onError={setError}
          onSaved={() => showToast("Video gespeichert")}
        />
      )}

      {openLesson && (
        <LessonDialog
          key={openLesson.lesson.id}
          lesson={openLesson.lesson}
          onClose={() => setOpenLesson(null)}
          onUpdated={onLessonUpdated}
          onDeleted={() => {
            deleteLesson(openLesson.sectionId, openLesson.lesson);
            setOpenLesson(null);
          }}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 rounded-md bg-foreground px-4 py-2 text-sm text-background shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

/**
 * Ticket W15: Pflege eines Kompaktkurses. Sichtbar sind nur Videoadresse und
 * Dauer; Abschnitte und Lektionen bleiben verborgen, weil ein Kompaktkurs aus
 * genau einem Video besteht. Inhalt und Materialien liegen weiterhin im
 * Lektions-Dialog, erreichbar über denselben Block.
 */
function CompactVideoBlock({
  lesson,
  onUpdated,
  onOpenDetails,
  onError,
  onSaved,
}: {
  lesson: LessonRow | null;
  onUpdated: (lesson: LessonRow) => void;
  onOpenDetails: (lesson: LessonRow) => void;
  onError: (message: string) => void;
  onSaved: () => void;
}) {
  const [vimeoUrl, setVimeoUrl] = useState(lesson?.vimeo_url ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setVimeoUrl(lesson?.vimeo_url ?? "");
  }, [lesson?.id, lesson?.vimeo_url]);

  if (!lesson) {
    return (
      <section className="rounded-lg border border-border bg-background p-5">
        <h2 className="text-lg font-semibold text-foreground">Video</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Das Video wird gleich angelegt. Laden Sie die Seite neu, falls hier nichts erscheint.
        </p>
      </section>
    );
  }

  const ref = parseVimeoRef(vimeoUrl);

  async function save() {
    setSaving(true);
    const { data, error } = await supabase
      .from("course_lessons")
      .update({ vimeo_url: vimeoUrl.trim() || null })
      .eq("id", lesson!.id)
      .select("*")
      .single();
    setSaving(false);
    if (error || !data) {
      onError(error?.message ?? "Video nicht gespeichert.");
      return;
    }
    onUpdated(data);
    onSaved();
  }

  return (
    <section className="rounded-lg border border-border bg-background p-5">
      <div className="mb-1 flex items-center gap-2">
        <Video className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-lg font-semibold text-foreground">Video</h2>
      </div>
      <p className="text-xs text-muted-foreground">
        Ein Kompaktkurs besteht aus genau einem Video. Abschnitte und Lektionen entfallen; die
        öffentliche Beschreibung ist das Beschreibungsfeld des Kurses.
      </p>

      <div className="mt-4 max-w-2xl space-y-3">
        <div>
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground" htmlFor="compact-vimeo">
            Videoadresse (Vimeo)
          </label>
          <input
            id="compact-vimeo"
            value={vimeoUrl}
            onChange={(e) => setVimeoUrl(e.target.value)}
            placeholder="https://vimeo.com/123456789"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {vimeoUrl.trim() === ""
              ? "Ohne Videoadresse bleibt der Kurs leer."
              : ref
                ? "Videoadresse erkannt."
                : "Diese Adresse wird nicht erkannt. Bitte prüfen Sie sie."}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Speichern
          </button>
          <button
            onClick={() => onOpenDetails(lesson!)}
            className="inline-flex items-center gap-2 rounded-md border border-input px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary"
          >
            <FileText className="h-4 w-4" /> Inhalt, Dauer und Materialien
          </button>
        </div>
      </div>
    </section>
  );
}

// ================== Section List ==================

function SectionList({
  sections,
  lessonsBySection,
  onReorderSections,
  onReorderLessons,
  onRenameSection,
  onDeleteSection,
  onAddLesson,
  onDeleteLesson,
  onOpenLesson,
}: {
  sections: SectionRow[];
  lessonsBySection: Record<string, LessonRow[]>;
  onReorderSections: (orderedIds: string[]) => void;
  onReorderLessons: (sectionId: string, orderedIds: string[]) => void;
  onRenameSection: (id: string, title: string) => void;
  onDeleteSection: (section: SectionRow) => void;
  onAddLesson: (sectionId: string) => void;
  onDeleteLesson: (sectionId: string, lesson: LessonRow) => void;
  onOpenLesson: (lesson: LessonRow, sectionId: string) => void;
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const ids = useMemo(() => sections.map((s) => s.id), [sections]);

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onReorderSections(arrayMove(ids, oldIndex, newIndex));
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ul className="space-y-3">
          {sections.map((section) => (
            <SectionItem
              key={section.id}
              section={section}
              lessons={lessonsBySection[section.id] ?? []}
              onReorderLessons={(orderedIds) => onReorderLessons(section.id, orderedIds)}
              onRenameSection={onRenameSection}
              onDeleteSection={onDeleteSection}
              onAddLesson={() => onAddLesson(section.id)}
              onDeleteLesson={(lesson) => onDeleteLesson(section.id, lesson)}
              onOpenLesson={(lesson) => onOpenLesson(lesson, section.id)}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function SectionItem({
  section,
  lessons,
  onReorderLessons,
  onRenameSection,
  onDeleteSection,
  onAddLesson,
  onDeleteLesson,
  onOpenLesson,
}: {
  section: SectionRow;
  lessons: LessonRow[];
  onReorderLessons: (orderedIds: string[]) => void;
  onRenameSection: (id: string, title: string) => void;
  onDeleteSection: (section: SectionRow) => void;
  onAddLesson: () => void;
  onDeleteLesson: (lesson: LessonRow) => void;
  onOpenLesson: (lesson: LessonRow) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const desc = useDescriptions();
  const [expanded, setExpanded] = useState(true);
  const [titleDraft, setTitleDraft] = useState(section.title);
  useEffect(() => setTitleDraft(section.title), [section.title]);

  return (
    <li ref={setNodeRef} style={style} className="rounded-md border border-border bg-secondary/20">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
          aria-label="Sektion verschieben"
          type="button"
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
          aria-label={expanded ? "Zuklappen" : "Aufklappen"}
          type="button"
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        <input
          value={titleDraft}
          onChange={(e) => setTitleDraft(e.target.value)}
          onBlur={() => {
            if (titleDraft !== section.title) onRenameSection(section.id, titleDraft.trim() || "Ohne Titel");
          }}
          className="flex-1 rounded-md border border-transparent bg-transparent px-2 py-1 text-sm font-semibold text-foreground focus:border-input focus:bg-background"
        />
        {!desc.show && (
          <>
            <DescriptionMark value={section.description} />
            {describedLabel(lessons) ? (
              <span className="text-xs text-muted-foreground">{describedLabel(lessons)}</span>
            ) : null}
          </>
        )}
        <span className="text-xs text-muted-foreground">{lessons.length} Lektion(en)</span>
        <button
          onClick={onAddLesson}
          className="inline-flex items-center gap-1 rounded-md bg-background px-2 py-1 text-xs font-medium text-foreground hover:bg-secondary"
          type="button"
        >
          <Plus className="h-3.5 w-3.5" /> Lektion
        </button>
        <button
          onClick={() => onDeleteSection(section)}
          className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          type="button"
          aria-label="Sektion löschen"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {desc.show && (
        <div className="px-3 pb-2 pl-14 pr-3">
          <DescriptionField
            fieldKey={`section:${section.id}`}
            value={section.description}
            ariaLabel={`Öffentliche Beschreibung des Abschnitts „${section.title || "Ohne Titel"}"`}
            placeholder="Öffentliche Beschreibung dieses Abschnitts (ein bis zwei Sätze)"
            onSave={(value) => desc.saveSection(section.id, value)}
          />
        </div>
      )}



      {expanded && (
        <div className="border-t border-border px-3 py-2">
          <LessonList
            lessons={lessons}
            onReorder={onReorderLessons}
            onOpen={onOpenLesson}
            onDelete={onDeleteLesson}
          />
          {lessons.length === 0 && (
            <div className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
              Noch keine Lektionen — mit „+ Lektion" anlegen.
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function LessonList({
  lessons,
  onReorder,
  onOpen,
  onDelete,
}: {
  lessons: LessonRow[];
  onReorder: (orderedIds: string[]) => void;
  onOpen: (lesson: LessonRow) => void;
  onDelete: (lesson: LessonRow) => void;
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const ids = useMemo(() => lessons.map((l) => l.id), [lessons]);

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(ids, oldIndex, newIndex));
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ul className="space-y-1.5">
          {lessons.map((lesson) => (
            <LessonRowItem
              key={lesson.id}
              lesson={lesson}
              onOpen={() => onOpen(lesson)}
              onDelete={() => onDelete(lesson)}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function LessonRowItem({
  lesson,
  onOpen,
  onDelete,
}: {
  lesson: LessonRow;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: lesson.id });
  const desc = useDescriptions();
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const hasVideo = !!lesson.vimeo_url && !!parseVimeoRef(lesson.vimeo_url);
  return (
    <li ref={setNodeRef} style={style} className="rounded-md border border-border bg-background px-2 py-1.5">
      <div className="flex items-center gap-2">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
          aria-label="Lektion verschieben"
          type="button"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={onOpen}
          className="flex flex-1 items-center gap-2 text-left text-sm text-foreground hover:text-primary"
          type="button"
        >
          {hasVideo ? <Video className="h-3.5 w-3.5 text-primary" /> : <FileText className="h-3.5 w-3.5 text-muted-foreground" />}
          <span className="flex-1 truncate">{lesson.title || "Ohne Titel"}</span>
        </button>
        {!desc.show && <DescriptionMark value={lesson.description} />}
        <button
          onClick={onDelete}
          className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          type="button"
          aria-label="Lektion löschen"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {desc.show && (
        <div className="mt-1.5 pl-8 pr-1">
          <DescriptionField
            fieldKey={`lesson:${lesson.id}`}
            value={lesson.description}
            ariaLabel={`Öffentliche Beschreibung der Lektion „${lesson.title || "Ohne Titel"}"`}
            placeholder="Öffentliche Beschreibung dieser Lektion (ein bis zwei Sätze)"
            onSave={(value) => desc.saveLesson(lesson, value)}
          />
        </div>
      )}
    </li>
  );
}

// ================== Ticket W13: öffentliche Beschreibungen ==================

/**
 * Zeichen für den zugeklappten Zustand: liegt eine Beschreibung vor?
 * Kein zweites Farbsignal, nur gefüllt gegen blass – plus Klartext für
 * Vorleseprogramme.
 */
function DescriptionMark({ value }: { value: string | null }) {
  const filled = hasDescription(value);
  return (
    <span
      className={filled ? "text-primary" : "text-muted-foreground/40"}
      title={filled ? "Beschreibung vorhanden" : "Beschreibung fehlt"}
    >
      <AlignLeft className="h-3.5 w-3.5" aria-hidden="true" />
      <span className="sr-only">{filled ? "Beschreibung vorhanden" : "Beschreibung fehlt"}</span>
    </span>
  );
}

/**
 * Flaches, mitwachsendes Feld. Gespeichert wird beim Verlassen, nur bei echter
 * Änderung. Der Serverwert wird nur übernommen, solange das Feld nicht den
 * Fokus hat – sonst überschriebe eine eintreffende Antwort einen halb
 * getippten Satz. Schlägt das Speichern fehl, bleibt die Eingabe stehen.
 */
function DescriptionField({
  fieldKey,
  value,
  ariaLabel,
  placeholder,
  onSave,
}: {
  fieldKey: string;
  value: string | null;
  ariaLabel: string;
  placeholder: string;
  onSave: (value: string | null) => Promise<boolean>;
}) {
  const { setDirty } = useDescriptions();
  const [draft, setDraft] = useState(value ?? "");
  const [failed, setFailed] = useState(false);
  const [focused, setFocused] = useState(false);
  const focusedRef = useRef(false);
  const draftRef = useRef(draft);
  const ref = useRef<HTMLTextAreaElement>(null);

  draftRef.current = draft;
  const dirty = isDescriptionDirty(draft, value);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  // Mitwachsende Höhe
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  // Serverwert nur ohne Fokus übernehmen
  useEffect(() => {
    if (!focusedRef.current) setDraft(value ?? "");
  }, [value]);

  const commit = useCallback(async () => {
    if (!dirtyRef.current) return;
    const ok = await onSave(normalizeDescription(draftRef.current));
    setFailed(!ok);
  }, [onSave]);

  // Offene Eingabe melden; beim Ausbau nichts hängen lassen
  useEffect(() => {
    setDirty(fieldKey, dirty);
    return () => setDirty(fieldKey, false);
  }, [fieldKey, dirty, setDirty]);

  // Verlässt die Seite den Vordergrund, wird die Eingabe gesichert
  useEffect(() => {
    function onHide() {
      if (document.visibilityState === "hidden") void commit();
    }
    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
  }, [commit]);

  const length = draft.trim().length;
  const over = isOverSoftLimit(draft);

  return (
    <div>
      <textarea
        ref={ref}
        value={draft}
        aria-label={ariaLabel}
        placeholder={placeholder}
        rows={1}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={() => {
          focusedRef.current = true;
          setFocused(true);
        }}
        onBlur={() => {
          focusedRef.current = false;
          setFocused(false);
          void commit();
        }}
        className={`w-full resize-none overflow-hidden rounded-md border bg-background px-2 py-1 text-xs leading-relaxed text-foreground ${
          failed ? "border-destructive" : "border-input"
        }`}
      />
      {(focused || over || failed) && (
        <div className="mt-0.5 flex items-center justify-between gap-2 text-[11px]">
          <span className={failed ? "text-destructive" : "text-muted-foreground"}>
            {failed
              ? "Noch nicht gespeichert — Feld erneut verlassen versucht es wieder."
              : "Öffentlich auf der Verkaufsseite. Speichert beim Verlassen."}
          </span>
          <span className={over ? "text-brand-accent" : "text-muted-foreground"}>
            {length}/{PUBLIC_DESCRIPTION_SOFT_LIMIT}
          </span>
        </div>
      )}
    </div>
  );
}


// ================== Lesson Editor Dialog ==================

function LessonDialog({
  lesson,
  onClose,
  onUpdated,
  onDeleted,
}: {
  lesson: LessonRow;
  onClose: () => void;
  onUpdated: (updated: LessonRow) => void;
  onDeleted: () => void;
}) {
  const [title, setTitle] = useState(lesson.title);
  const [vimeoUrl, setVimeoUrl] = useState(lesson.vimeo_url ?? "");
  const [bodyMd, setBodyMd] = useState(lesson.body_md ?? "");
  const [materials, setMaterials] = useState<MaterialRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const vimeoRef = useMemo(() => (vimeoUrl.trim() ? parseVimeoRef(vimeoUrl) : null), [vimeoUrl]);
  const vimeoValid = vimeoUrl.trim() === "" || !!vimeoRef;

  useEffect(() => {
    let cancelled = false;
    supabase
      .from("course_lesson_materials")
      .select("*")
      .eq("lesson_id", lesson.id)
      .order("sort_order", { ascending: true })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) setError(error.message);
        else setMaterials(data ?? []);
      });
    return () => { cancelled = true; };
  }, [lesson.id]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function save() {
    if (!vimeoValid) { setError("Vimeo-URL ist ungültig."); return; }
    setSaving(true);
    setError(null);
    const { data, error } = await supabase
      .from("course_lessons")
      .update({
        title: title.trim() || "Ohne Titel",
        vimeo_url: vimeoUrl.trim() || null,
        body_md: bodyMd,
      })
      .eq("id", lesson.id)
      .select("*")
      .single();
    setSaving(false);
    if (error) { setError(error.message); return; }
    if (data) {
      onUpdated(data);
      onClose();
    }
  }

  async function onMaterialUpload(file: File) {
    setError(null);
    if (file.size > MATERIAL_MAX_BYTES) {
      setError("Datei ist größer als 25 MB.");
      return;
    }
    if (file.type && !MATERIAL_ALLOWED.includes(file.type)) {
      setError(`Dateityp ${file.type} ist nicht erlaubt.`);
      return;
    }
    const ext = file.name.split(".").pop()?.toLowerCase() || "bin";
    const path = `${lesson.course_id}/${lesson.id}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const { error: upErr } = await supabase.storage.from("course-materials").upload(path, file);
    if (upErr) { setError(upErr.message); return; }
    const nextOrder = materials.length > 0 ? Math.max(...materials.map((m) => m.sort_order)) + 1 : 0;
    const { data, error } = await supabase
      .from("course_lesson_materials")
      .insert({
        lesson_id: lesson.id,
        label: file.name,
        storage_path: path,
        mime_type: file.type || null,
        size_bytes: file.size,
        sort_order: nextOrder,
      })
      .select("*")
      .single();
    if (error) { setError(error.message); return; }
    if (data) setMaterials((prev) => [...prev, data]);
  }

  async function renameMaterial(mat: MaterialRow, label: string) {
    const { data, error } = await supabase
      .from("course_lesson_materials")
      .update({ label })
      .eq("id", mat.id)
      .select("*")
      .single();
    if (error) { setError(error.message); return; }
    if (data) setMaterials((prev) => prev.map((m) => (m.id === data.id ? data : m)));
  }

  async function removeMaterial(mat: MaterialRow) {
    if (
      !(await confirmDialog({
        title: `Material „${mat.label}" löschen?`,
        description: "Die Datei wird ebenfalls aus dem Speicher entfernt.",
      }))
    )
      return;
    const { error } = await supabase.from("course_lesson_materials").delete().eq("id", mat.id);
    if (error) { setError(error.message); return; }
    await supabase.storage.from("course-materials").remove([mat.storage_path]);
    setMaterials((prev) => prev.filter((m) => m.id !== mat.id));
  }

  async function openMaterial(mat: MaterialRow) {
    const { data, error } = await supabase.storage.from("course-materials").createSignedUrl(mat.storage_path, 60 * 5);
    if (error || !data?.signedUrl) { setError(error?.message ?? "Konnte URL nicht erzeugen."); return; }
    window.open(data.signedUrl, "_blank", "noopener");
  }

  const iframeSrc = vimeoRef
    ? `https://player.vimeo.com/video/${vimeoRef.videoId}${vimeoRef.hash ? `?h=${vimeoRef.hash}` : ""}`
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-foreground/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div ref={dialogRef} className="mt-8 w-full max-w-3xl rounded-lg bg-background shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h3 className="text-base font-semibold text-foreground">Lektion bearbeiten</h3>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground" aria-label="Schließen">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Titel</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-[1fr_140px]">
            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Vimeo-URL</label>
              <input
                value={vimeoUrl}
                onChange={(e) => setVimeoUrl(e.target.value)}
                placeholder="https://vimeo.com/123456789/abcdef"
                className={`mt-1 w-full rounded-md border bg-background px-3 py-2 font-mono text-sm ${
                  vimeoValid ? "border-input" : "border-destructive"
                }`}
              />
              {!vimeoValid && (
                <p className="mt-1 text-xs text-destructive">URL ist keine gültige Vimeo-Adresse.</p>
              )}
            </div>
            {/*
              Ticket AV6: Eingabefeld „Dauer" entfallen (bei 75 Lektionen nie belegt) —
              die Laufzeit ergibt sich aus dem Video. Die Spalte bleibt bestehen, weil
              das Cockpit vorhandene Werte weiterhin anzeigt.
            */}
          </div>

          {iframeSrc && (
            <div className="overflow-hidden rounded-md border border-border">
              <div className="relative aspect-video bg-black">
                <iframe
                  src={iframeSrc}
                  className="absolute inset-0 h-full w-full"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title="Vimeo-Vorschau"
                  loading="lazy"
                />
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Lektionsinhalt für Kundinnen und Kunden (Markdown)
            </label>
            <p className="mt-1 text-xs text-muted-foreground">
              Nur im Kundencockpit sichtbar. Die öffentliche Beschreibung für die
              Verkaufsseite wird in der Struktur-Ansicht gepflegt.
            </p>

            <textarea
              value={bodyMd}
              onChange={(e) => setBodyMd(e.target.value)}
              rows={8}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
              placeholder="Zusammenfassung, Lernziele, weiterführende Links…"
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Materialien</label>
              <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-input px-2 py-1 text-xs hover:bg-secondary">
                <Upload className="h-3.5 w-3.5" /> Datei hinzufügen
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onMaterialUpload(f);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
            {materials.length === 0 ? (
              <div className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
                Keine Materialien angehängt.
              </div>
            ) : (
              <ul className="space-y-1.5">
                {materials.map((m) => (
                  <li key={m.id} className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5">
                    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                    <input
                      defaultValue={m.label}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v && v !== m.label) renameMaterial(m, v);
                      }}
                      className="flex-1 rounded-md border border-transparent bg-transparent px-2 py-1 text-sm text-foreground focus:border-input focus:bg-background"
                    />
                    <button
                      onClick={() => openMaterial(m)}
                      className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
                      type="button"
                      aria-label="Öffnen"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => removeMaterial(m)}
                      className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      type="button"
                      aria-label="Löschen"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-1 text-xs text-muted-foreground">Max. 25 MB. PDF, Office, Bilder oder ZIP.</p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border bg-secondary/30 px-5 py-3">
          <button
            onClick={onDeleted}
            className="inline-flex items-center gap-1 rounded-md border border-destructive/40 px-3 py-1.5 text-xs text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-3.5 w-3.5" /> Lektion löschen
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="rounded-md border border-input px-4 py-2 text-sm text-foreground hover:bg-secondary"
            >
              Abbrechen
            </button>
            <button
              onClick={save}
              disabled={saving || !vimeoValid}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Speichern
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
